import { useState } from 'react';

import { Badge, Button, Divider, Modal, Typography } from 'antd';
import {
	ArrowLeftOutlined,
	DownOutlined,
	RightOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import BuildingIndicator from '../../components/BuildingIndicator';
import { useBuildingContext } from '../../context/BuildingContext';
import { OPERATION_DESCRIPTIONS } from '../../components/Operations/tabletStyles';
import { KT_PILOT_URL, isPilotEnabled } from '../../config/ktPilot';
import { useGetOpenMaterialNeedCountQuery } from '../../redux/api/shop-floor-needs';

const { Title, Text, Paragraph } = Typography;

type DescriptionKey = keyof typeof OPERATION_DESCRIPTIONS;

// Only Reservations carries the open-request count. Cutting used to show it
// too, but the same number on two tiles read as two separate queues.
const NEED_BADGE_TILE = 'reservations';

const TILES: { key: DescriptionKey; label: string; path: string }[] = [
	{
		key: 'cutting',
		label: 'Cutting',
		path: 'cutting?group_cut_status=Pending',
	},
	{ key: 'receiving', label: 'Receiving', path: 'receiving' },
	{ key: 'kitting', label: 'Kitting', path: 'kitting?kitting=Pending' },
	{
		key: 'delivery',
		label: 'Delivery',
		path: 'delivery?delivery_status=Pending',
	},
	{ key: 'material', label: 'Material', path: 'material-lengths' },
	{ key: 'reservations', label: 'Reservations', path: 'reservations' },
	{
		key: 'jawConfiguration',
		label: 'Jaw Configuration',
		path: 'jaw-configuration',
	},
	{ key: 'kitStaging', label: 'Kit Tracking', path: 'kit-staging' },
];

const Operations: React.FC = () => {
	const [showQcOptions, setShowQcOptions] = useState(false);
	const [infoKey, setInfoKey] = useState<DescriptionKey | null>(null);
	const navigate = useNavigate();
	// Same building scope as the hot list itself, so the badge never promises
	// requests the list will not show.
	const { buildingId } = useBuildingContext();
	const { data: openNeeds } = useGetOpenMaterialNeedCountQuery(
		buildingId ? { building_id: buildingId } : undefined,
		{ pollingInterval: 30000 }
	);

	const infoEntry = infoKey ? OPERATION_DESCRIPTIONS[infoKey] : null;

	const renderInfoIcon = (key: DescriptionKey) => (
		<div
			style={{
				position: 'absolute',
				top: 10,
				right: 10,
				zIndex: 1,
				width: 40,
				height: 40,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				borderRadius: '50%',
				background: 'rgba(255,255,255,0.18)',
				cursor: 'pointer',
			}}
			onClick={(e) => {
				e.stopPropagation();
				setInfoKey(key);
			}}
		>
			<InfoCircleOutlined style={{ fontSize: 24, color: '#fff' }} />
		</div>
	);

	return (
		<div className="flex flex-col w-full" style={{ padding: '0 24px' }}>
			<div
				className="flex justify-between items-center"
				style={{ marginTop: 20 }}
			>
				<div className="flex items-center gap-3">
					<Title level={2} style={{ margin: 0 }}>
						Fastlane Operations
					</Title>
					<BuildingIndicator />
					{isPilotEnabled() && (
						// In the header rather than under the Kit Tracking tile: the
						// tile grid already fills a 1280x800 tablet, so anything below
						// it lands off the fold with no cue to scroll.
						<a
							href={KT_PILOT_URL}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								minHeight: 44,
								padding: '0 12px',
								fontSize: 15,
								fontWeight: 600,
							}}
						>
							Try the new Kit Tracking →
						</a>
					)}
				</div>
				<Button
					type="text"
					style={{ fontSize: 24 }}
					onClick={() => navigate('/')}
				>
					<ArrowLeftOutlined />
					<span>Back</span>
				</Button>
			</div>

			<Divider style={{ margin: '12px 0' }} />

			<div
				className="flex flex-wrap"
				style={{ justifyContent: 'center', gap: 120, marginTop: 50 }}
			>
				{/* Quality Control tile with expandable sub-options */}
				<div className="flex flex-col items-center" style={{ gap: 12 }}>
					<div style={{ position: 'relative' }}>
						{renderInfoIcon('qualityControl')}
						<Button
							type="primary"
							className="op-home-buttons"
							onClick={() => setShowQcOptions(!showQcOptions)}
						>
							Quality Control
							{showQcOptions ? (
								<DownOutlined style={{ fontSize: 16, marginLeft: 8 }} />
							) : (
								<RightOutlined style={{ fontSize: 16, marginLeft: 8 }} />
							)}
						</Button>
					</div>

					{showQcOptions && (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: 10,
								padding: '12px 16px',
								border: '1px solid rgba(255,255,255,0.15)',
								borderRadius: 8,
								background: 'rgba(28,42,56,0.06)',
							}}
						>
							<Button
								type="primary"
								className="op-home-buttons"
								onClick={() => navigate('qc/keyence')}
							>
								Keyence
							</Button>
							<Button
								type="primary"
								className="op-home-buttons"
								onClick={() => navigate('qc/machine')}
							>
								Machine
							</Button>
						</div>
					)}
				</div>

				{/* Standard operation tiles */}
				{TILES.map(({ key, label, path }) => (
					<div key={key} style={{ position: 'relative' }}>
						{renderInfoIcon(key)}
						<Badge
							count={key === NEED_BADGE_TILE ? openNeeds?.open || 0 : 0}
							offset={[-12, 12]}
							style={{ backgroundColor: '#fa8c16' }}
						>
							<Button
								type="primary"
								className="op-home-buttons"
								onClick={() => navigate(path)}
							>
								{label}
							</Button>
						</Badge>
					</div>
				))}
			</div>

			{/* Info modal for operation descriptions */}
			<Modal
				open={!!infoEntry}
				title={infoEntry?.title}
				footer={null}
				onCancel={() => setInfoKey(null)}
				centered
				width={480}
			>
				{infoEntry && (
					<>
						<Paragraph style={{ fontSize: 15, marginTop: 8 }}>
							{infoEntry.description}
						</Paragraph>
						<Text type="secondary" style={{ fontSize: 13 }}>
							Typically used by: {infoEntry.usedBy}
						</Text>
					</>
				)}
			</Modal>
		</div>
	);
};

export default Operations;
